import { monthEndOf, monthStartOf } from "@couples/shared";

import { supabase } from "../supabase";

/** 1ページの件数。3列グリッドで20行、スマホで約3画面分 */
export const PAGE_SIZE = 60;

export interface TimelineItem {
  id: string;
  type: "photo" | "video";
  thumbnail_key: string;
  storage_key: string;
  duration_seconds: number | null;
  taken_at: string;
  uploader_id: string;
}

const COLUMNS = "id, type, thumbnail_key, storage_key, duration_seconds, taken_at, uploader_id";

/** 一覧の絞り込み。すべて / タグ / タグなし / 自分のお気に入り */
export type TimelineFilter =
  | { kind: "all" }
  | { kind: "tag"; id: string }
  | { kind: "untagged" }
  | { kind: "favorite" };

/** 種別の絞り込み。タグや月の起点とは独立した軸 */
export type MediaTypeFilter = "photo" | "video" | null;

/** 誰が追加したかの絞り込み。プロフィールの id。null は二人ぶん(絞り込まない) */
export type UploaderFilter = string | null;

export interface TimelineCursor {
  takenAt: string;
  id: string;
}

export interface TimelinePage {
  items: TimelineItem[];
  next: TimelineCursor | null;
}

/**
 * タイムラインを1ページ取得する。
 *
 * オフセットではなくカーソルで進める。スクロール中に写真が増減しても
 * 表示がずれたり同じ写真が二重に出たりしない
 * (→ docs/architecture/tech-stack.md「タイムラインの読み込み」)。
 */
export async function fetchTimelinePage(
  cursor: TimelineCursor | null,
  filter: TimelineFilter = { kind: "all" },
  type: MediaTypeFilter = null,
  month: string | null = null,
  uploader: UploaderFilter = null,
): Promise<TimelinePage> {
  // 絞り込みは内部結合で行う。
  // !inner を付けないと、タグやお気に入りが無いメディアも残ってしまう。
  // お気に入りは RLS が自分の行しか返さないため、誰のものかは指定しなくてよい。
  // 「タグなし」は逆に外部結合し、結び付く行が無いものだけを残す。
  const columns =
    filter.kind === "tag"
      ? `${COLUMNS}, media_tags!inner(tag_id)`
      : filter.kind === "untagged"
        ? `${COLUMNS}, media_tags!left(tag_id)`
        : filter.kind === "favorite"
          ? `${COLUMNS}, media_favorites!inner(user_id)`
          : COLUMNS;

  let query = supabase
    .from("media")
    .select(columns)
    .is("deleted_at", null)
    .order("taken_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(PAGE_SIZE);

  if (filter.kind === "tag") {
    query = query.eq("media_tags.tag_id", filter.id);
  }
  if (filter.kind === "untagged") {
    query = query.is("media_tags", null);
  }
  if (type !== null) {
    query = query.eq("type", type);
  }
  if (uploader !== null) {
    query = query.eq("uploader_id", uploader);
  }
  if (month !== null) {
    // 月の指定は「その月だけ」。以前は起点(以降を読む)だったが、
    // 探したいのはその月なので範囲で切る
    query = query.gte("taken_at", monthStartOf(month)).lt("taken_at", monthEndOf(month));
  }

  if (cursor !== null) {
    // (taken_at, id) の組で比較する。同じ秒の写真が複数あっても取りこぼさない
    query = query.or(
      `taken_at.lt.${cursor.takenAt},and(taken_at.eq.${cursor.takenAt},id.lt.${cursor.id})`,
    );
  }

  const { data, error } = await query;
  if (error) throw error;

  const items = data as unknown as TimelineItem[];
  const last = items.at(-1);
  return {
    items,
    // 満たなければ最後のページ
    next:
      items.length === PAGE_SIZE && last !== undefined
        ? { takenAt: last.taken_at, id: last.id }
        : null,
  };
}

/**
 * 絞り込み後の総件数。詳細表示の「n / 全体」に使う。
 * month を渡すとその月の中だけ、uploader を渡すとその人が追加したぶんだけを数える。
 */
export async function countTimeline(
  filter: TimelineFilter = { kind: "all" },
  type: MediaTypeFilter = null,
  month: string | null = null,
  uploader: UploaderFilter = null,
): Promise<number> {
  const columns =
    filter.kind === "tag"
      ? "id, media_tags!inner(tag_id)"
      : filter.kind === "untagged"
        ? "id, media_tags!left(tag_id)"
        : filter.kind === "favorite"
          ? "id, media_favorites!inner(user_id)"
          : "id";

  let query = supabase
    .from("media")
    .select(columns, { count: "exact", head: true })
    .is("deleted_at", null);

  if (filter.kind === "tag") {
    query = query.eq("media_tags.tag_id", filter.id);
  }
  if (filter.kind === "untagged") {
    query = query.is("media_tags", null);
  }
  if (type !== null) {
    query = query.eq("type", type);
  }
  if (uploader !== null) {
    query = query.eq("uploader_id", uploader);
  }
  if (month !== null) {
    query = query.gte("taken_at", monthStartOf(month)).lt("taken_at", monthEndOf(month));
  }

  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

function isMonthCount(value: unknown): value is { month: string; media_count: number } {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  return typeof row.month === "string" && typeof row.media_count === "number";
}

/**
 * 月ヘッダーに出す全件数。
 * 無限スクロールでは読み込み済みの件数しか分からないため別途取る。
 *
 * DBの型を生成していないので rpc の戻りは any になる。
 * 型注釈で覆うと実態と食い違ったときに気づけないため、実行時に検証する。
 */
export async function fetchMonthCounts(): Promise<Map<string, number>> {
  const response = await supabase.rpc("media_month_counts");
  if (response.error) throw response.error;

  const rows: unknown = response.data;
  if (!Array.isArray(rows)) return new Map();

  return new Map(rows.filter(isMonthCount).map((row) => [row.month, row.media_count]));
}
